import React, { useEffect, useState } from "react";
import styles from "./CreatedDateFloatingFilter.module.css";

const getDisplayValueFromModel = (model: any) => {
  const type = model?.type;

  const from = toDisplayDate(model?.dateFrom || model?.filter);
  const to = toDisplayDate(model?.dateTo || model?.filterTo);

  if (type === "inRange") {
    if (from && to) return `${from} - ${to}`;
    if (from) return `Between ${from}`;
    return "";
  }

  if (type === "equals" && from) {
    return `Equals ${from}`;
  }

  if (type === "greaterThan" && from) {
    return `After ${from}`;
  }

  if (type === "lessThan" && from) {
    return `Before ${from}`;
  }

  return from || to || "";
};

type Props = {
  api?: any;
  column?: any;
  showParentFilter?: () => void;
};

const toDisplayDate = (value?: string) => {
  if (!value) return "";

  const datePart = String(value).split(" ")[0];
  const [yyyy, mm, dd] = datePart.split("-");

  if (!yyyy || !mm || !dd) return "";

  return `${mm}/${dd}/${yyyy}`;
};

const formatForGridModel = (dateValue: string) => {
  const trimmed = dateValue.trim();

  if (!trimmed) return "";

  // Supports yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return `${trimmed} 00:00:00`;
  }

  // Supports mm/dd/yyyy
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
    const [mm, dd, yyyy] = trimmed.split("/");

    return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")} 00:00:00`;
  }

  return "";
};

const CreatedDateFloatingFilter: React.FC<Props> = (props) => {
  const [value, setValue] = useState("");

  const colId = props.column?.getColId?.() || "createdDate";

  const syncFromGridModel = () => {
    const model = props.api?.getFilterModel?.();
    const createdDateModel = model?.[colId];

    if (!createdDateModel) {
      setValue("");
      return;
    }

    setValue(getDisplayValueFromModel(createdDateModel));
  };

  useEffect(() => {
    syncFromGridModel();

    props.api?.addEventListener?.("filterChanged", syncFromGridModel);

    return () => {
      props.api?.removeEventListener?.("filterChanged", syncFromGridModel);
    };
  }, [props.api, colId]);

  const clearFilter = () => {
    const currentModel = props.api?.getFilterModel?.() || {};
    delete currentModel[colId];

    props.api?.setFilterModel?.(currentModel);
    props.api?.onFilterChanged?.();
    setValue("");
  };

  const handleChange = (nextValue: string) => {
    setValue(nextValue);

    if (!nextValue.trim()) {
      clearFilter();
      return;
    }

    const formattedDate = formatForGridModel(nextValue);

    // Do not apply until full valid date is entered
    if (!formattedDate) return;

    const currentModel = props.api?.getFilterModel?.() || {};

    props.api?.setFilterModel?.({
      ...currentModel,
      [colId]: {
        filterType: "date",
        type: "equals",
        dateFrom: formattedDate,
      },
    });

    props.api?.onFilterChanged?.();
  };

  return (
    <div className={styles.wrapper}>
      <input
        className={styles.input}
        type="text"
        placeholder="Select Date Filter"
        value={value}
        onChange={(event) => handleChange(event.target.value)}
        onClick={() => props.showParentFilter?.()}
      />
    </div>
  );
};

export default CreatedDateFloatingFilter;